// CSV/TSV data-aware diff. Rows are matched by KEY, not position (the analyst
// wedge). PapaParse + a key mapping in front of the shared record-set result
// builder (records-result.js) — the SAME path JSON arrays now use.
import Papa from 'papaparse';
import { toRecordSet } from './records/model.js';
import { buildRecordsResult } from './records/records-result.js';
import { hasDuplicateKeys } from './records/keys.js';
import { rawLineRows } from './line-diff.js';
import { diffText } from './text-diff.js';

export function diffCsv(payload = {}) {
  const { left = '', right = '', options = {}, keyColumn } = payload;
  const format = payload.format === 'tsv' ? 'tsv' : 'csv';

  const lp = parse(left, format);
  const rp = parse(right, format);
  if (lp.errors.length || rp.errors.length || !lp.data.length || !rp.data.length || !lp.fields.length) {
    const fallback = diffText(payload);
    const errors = [...lp.errors, ...rp.errors];
    fallback.fallback = {
      from: format,
      ...(errors.length ? { reason: errors.map((e) => `${e.code}: ${e.message}`).join('; ') } : {}),
    };
    return fallback;
  }

  const union = [...new Set([...lp.fields, ...(rp.fields || [])])];
  const keyName = keyColumn && union.includes(keyColumn) ? keyColumn : lp.fields[0];

  let lm = toRecordSet(lp.data, { keyColumns: [keyName] });
  let rm = toRecordSet(rp.data, { keyColumns: [keyName] });

  // Duplicate keys would silently drop a row — fall back to positional matching
  // and warn, rather than merge two records into one.
  let warning = null;
  let keyCol = keyName;
  if (hasDuplicateKeys(lm) || hasDuplicateKeys(rm)) {
    lm = toRecordSet(lp.data, { keyColumns: [] });
    rm = toRecordSet(rp.data, { keyColumns: [] });
    warning = { kind: 'duplicate-key', key: keyName };
    keyCol = null; // matched by row position now — the id is just another cell
  }

  return buildRecordsResult({
    lm,
    rm,
    options,
    leftCols: lp.fields,
    rightCols: rp.fields || [],
    keyCol,
    format,
    rawRows: rawLineRows(left, right, options),
    warning,
  });
}

// A header that cannot survive `row[name] = value` on a plain object. Exactly one
// exists: `__proto__` is an ACCESSOR on Object.prototype, so the assignment runs a
// setter and stores nothing. Every other inherited name (`toString`, `constructor`,
// `hasOwnProperty`…) is a writable data property, so assigning it creates a normal
// own property that shadows it — those are already safe.
const unsafeHeader = (name) => name === '__proto__';

// Preserve cell lexemes exactly. Numeric-looking identifiers such as `01`
// are data, not JavaScript numbers, and whitespace is only ignorable when the
// caller explicitly enables that comparison option. Exported so the key-column
// picker reads the header with THIS config and offers only names the engine has.
export function csvParseConfig(format) {
  return {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: false,
    ...(format === 'tsv' ? { delimiter: '\t' } : {}),
  };
}

function parse(text, format) {
  const config = csvParseConfig(format);
  const out = Papa.parse(text, config);
  // A one-column CSV has no delimiter to detect, which Papa reports as an
  // UndetectableDelimiter warning even though the data is valid. All other
  // parser errors (bad quotes, row-width mismatches, etc.) are unsafe to feed
  // into the semantic record matcher and therefore trigger the text fallback.
  const errors = (out.errors || []).filter((error) => error.code !== 'UndetectableDelimiter');
  const fields = out.meta.fields || [];
  // REL-082. `header:true` builds each row as a PLAIN object, so a column named
  // `__proto__` loses every value on the way in while `meta.fields` still reports
  // the column — cruxdiff announced the column existed and then found no change in
  // it, whatever the values were. The records model was already null-prototype
  // (`flatten.js`, `model.js`, 3 Sep), so this was the one remaining step that
  // could not hold the key; JSON and YAML were never affected because JSON.parse
  // creates it as an own property. Papa has no option for this, so the rare file
  // that declares such a column is re-read as ARRAYS and rebuilt on null-prototype
  // rows. Errors and fields still come from the parse above, so nothing about the
  // ordinary path — the one every real file takes — changes.
  const data = fields.some(unsafeHeader) ? rowsFromArrays(text, config, fields) : out.data;
  return { data, fields, errors };
}

function rowsFromArrays(text, config, fields) {
  const [, ...body] = Papa.parse(text, { ...config, header: false }).data;
  return body.map((cells) => {
    const row = Object.create(null);
    // Assign only cells that exist, matching what header mode produces for a
    // short row (which also raises TooFewFields, so such a file falls back to
    // the text engine before it reaches the record matcher anyway).
    fields.forEach((name, i) => {
      if (i < cells.length) row[name] = cells[i];
    });
    return row;
  });
}
